import { useState } from 'react';
import { App, Button, Empty, Popconfirm, Spin, Tag } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import type { HistoryEntry } from '@core/entities/History';
import type { RequestExample } from '@core/entities/RequestExample';
import {
  useDeleteRequestExample,
  useRenameRequestExample,
  useRequestExamples,
} from '@presentation/hooks/useRequestExamples';
import { ResponseViewer } from '@presentation/components/workbench/ResponseViewer';
import { NamePromptModal } from '@presentation/components/workbench/NamePromptModal';

interface ExamplesPanelProps {
  requestId: string | null;
}

const statusColor = (statusCode: number): string => {
  if (statusCode < 300) return 'success';
  if (statusCode < 400) return 'processing';
  if (statusCode < 500) return 'warning';
  return 'error';
};

const toSyntheticEntry = (example: RequestExample): HistoryEntry => ({
  id: example.id,
  workspaceId: '',
  requestId: example.requestId,
  name: example.name,
  method: 'GET',
  url: '',
  statusCode: example.statusCode,
  durationMs: null,
  responseSizeBytes: example.responseBody ? example.responseBody.length : null,
  errorMessage: null,
  executedAt: example.createdAt,
  user: example.createdBy,
  response: {
    id: example.id,
    statusCode: example.statusCode,
    statusText: example.statusText,
    headers: example.responseHeaders,
    body: example.responseBody,
    cookies: null,
    durationMs: 0,
    sizeBytes: example.responseBody ? example.responseBody.length : 0,
    createdAt: example.createdAt,
  },
  isFavorited: false,
  abilities: { delete: false },
});

export const ExamplesPanel = ({ requestId }: ExamplesPanelProps) => {
  const { message } = App.useApp();
  const { data: examples, isLoading } = useRequestExamples(requestId ?? undefined);
  const renameExample = useRenameRequestExample(requestId ?? '');
  const deleteExample = useDeleteRequestExample(requestId ?? '');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<RequestExample | null>(null);

  if (!requestId) {
    return (
      <Empty
        className="mt-6"
        description="Save this request first, then send it to record examples"
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spin size="small" />
      </div>
    );
  }

  const selected = examples?.find((example) => example.id === selectedId) ?? null;

  if (selected) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-2 border-b border-black/10 p-2 dark:border-white/10">
          <Button size="small" onClick={() => setSelectedId(null)}>
            ← Back to examples
          </Button>
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{selected.name}</span>
        </div>
        <div className="min-h-0 flex-1">
          <ResponseViewer entry={toSyntheticEntry(selected)} isSending={false} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-auto p-2">
      {(!examples || examples.length === 0) && (
        <Empty
          className="mt-6"
          description="No examples yet — send a request and click “Save as Example”"
        />
      )}

      {examples?.map((example) => (
        <div
          key={example.id}
          role="button"
          tabIndex={0}
          onClick={() => setSelectedId(example.id)}
          className="group flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
        >
          <Tag color={statusColor(example.statusCode)} className="shrink-0">
            {example.statusCode}
          </Tag>
          <span className="min-w-0 flex-1 truncate">{example.name}</span>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              setRenaming(example);
            }}
            className="shrink-0 opacity-0 group-hover:opacity-100"
            aria-label="Rename example"
          />
          <Popconfirm
            title="Delete this example?"
            onConfirm={() =>
              deleteExample.mutate(example.id, {
                onSuccess: () => void message.success('Example deleted'),
              })
            }
          >
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={(event) => event.stopPropagation()}
              className="shrink-0 opacity-0 group-hover:opacity-100"
              aria-label="Delete example"
            />
          </Popconfirm>
        </div>
      ))}

      <NamePromptModal
        open={renaming !== null}
        title="Rename example"
        initialValue={renaming?.name ?? ''}
        confirmLoading={renameExample.isPending}
        onCancel={() => setRenaming(null)}
        onSubmit={(name) => {
          if (!renaming) return;
          renameExample.mutate(
            { id: renaming.id, name, description: renaming.description },
            {
              onSuccess: () => {
                void message.success('Example renamed');
                setRenaming(null);
              },
            },
          );
        }}
      />
    </div>
  );
};
